import React from "react"
import { Avatar } from "antd"
import {
	TeamOutlined,
	RobotOutlined,
	BellOutlined,
	NotificationOutlined,
	InboxOutlined,
} from "@ant-design/icons"
import { Group } from "firebase/groupsService"
import { getAvatarColors, formatTime } from "../utils"
import strings from "utils/strings"

interface GroupItemProps {
	group: Group
	loggedUserId: string
	isSelected: boolean
	isArchived?: boolean
	onSelect: (group: Group) => void
}

const notifConfig = {
	all: {
		icon: BellOutlined,
		color: "#A0AEC0",
		label: () => strings.messaging.notifAllShort,
	},
	mentions: {
		icon: NotificationOutlined,
		color: "#F39C12",
		label: () => strings.messaging.notifMentionsShort,
	},
	none: {
		icon: BellOutlined,
		color: "#CBD5E0",
		label: () => strings.messaging.notifNoneShort,
	},
} as const

export const GroupItem: React.FC<GroupItemProps> = ({
	group,
	loggedUserId,
	isSelected,
	isArchived,
	onSelect,
}) => {
	const preview = group.lastMessage
	const [c1, c2] = getAvatarColors(group.name)
	const notifPref = (group.notifPrefs?.[loggedUserId] ??
		"all") as keyof typeof notifConfig
	const {
		icon: NotifIcon,
		color: notifColor,
		label: notifLabel,
	} = notifConfig[notifPref] ?? notifConfig.all

	const myLastRead = group.lastReadAt?.[loggedUserId]
	const hasUnread =
		!isArchived &&
		!!group.updatedAt &&
		(!myLastRead || group.updatedAt > myLastRead)

	return (
		<div
			className={`group-item${isSelected ? " group-item--selected" : ""}${hasUnread ? " group-item--unread" : ""}${isArchived ? " group-item--archived" : ""}`}
			onClick={() => onSelect(group)}
		>
			<div className="group-item__avatar">
				{isArchived ? (
					<div
						style={{
							width: 42,
							height: 42,
							borderRadius: "50%",
							background: "linear-gradient(135deg, #A0AEC0, #CBD5E0)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "#fff",
							fontSize: 16,
							flexShrink: 0,
						}}
					>
						<InboxOutlined />
					</div>
				) : (
					<Avatar
						size={42}
						style={{
							background: `linear-gradient(135deg, ${c1}, ${c2})`,
							fontSize: 16,
							fontWeight: 700,
							color: "#fff",
							flexShrink: 0,
						}}
					>
						{group.name[0]?.toUpperCase()}
					</Avatar>
				)}
				{!isArchived && group.hasBotActive && (
					<div className="group-item__bot-badge">
						<RobotOutlined style={{ fontSize: 8 }} />
					</div>
				)}
				{hasUnread && <div className="group-item__unread-dot" />}
			</div>
			<div className="group-item__info">
				<div className="group-item__header-row">
					<span className="group-item__name">{group.name}</span>
					{group.updatedAt && (
						<span className="group-item__time">
							{formatTime(group.updatedAt)}
						</span>
					)}
				</div>
				<div className="group-item__preview-row">
					<span className="group-item__preview">
						{isArchived ? (
							<em style={{ opacity: 0.55 }}>
								{strings.messaging.archivedBadge}
							</em>
						) : preview ? (
							`${group.lastMessageSenderName ? group.lastMessageSenderName + ": " : ""}${preview}`
						) : (
							<em style={{ opacity: 0.6 }}>
								{strings.messaging.noMessagesPreview}
							</em>
						)}
					</span>
					{!isArchived && (
						<span className="group-item__meta">
							<span className="group-item__members">
								<TeamOutlined style={{ fontSize: 9, marginRight: 2 }} />
								{group.memberCount}
							</span>
							<span
								className="group-item__notif-badge"
								style={{ color: notifColor }}
							>
								<NotifIcon style={{ fontSize: 9, marginRight: 2 }} />
								{notifLabel()}
							</span>
						</span>
					)}
				</div>
			</div>
		</div>
	)
}
